import { MapPin, Phone, Mail, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { CONTACT } from '@/lib/constants'

// Real site footer contact section — rendered as a pre-footer section here.
// Matches the real site's footer info row:
//   background: white (body area before cream footer)
//   3-col layout: contact info | map embed | hours
//   Google Maps embed for Hotel Boutique Nueva Valladolid, Comayagua
// Section heading style: uppercase Montserrat, navy #0F2966

export function LocationMap() {
  const { t, i18n } = useTranslation()
  const es = i18n.language.startsWith('es')

  return (
    <section
      className="py-20 lg:py-24"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="mx-auto max-w-7xl px-10">
        {/* Section heading */}
        <AnimatedSection className="mb-10 text-center">
          <h2
            className="text-3xl font-bold uppercase tracking-wide"
            style={{ color: '#0F2966' }}
          >
            {t('contact.info_title')}
          </h2>
        </AnimatedSection>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Google Maps embed — 2/3 width on desktop */}
          <AnimatedSection className="lg:col-span-2">
            <div
              className="overflow-hidden"
              style={{ aspectRatio: '16/9', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
            >
              <iframe
                src={CONTACT.googleMapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hotel Boutique Nueva Valladolid — Comayagua"
              />
            </div>
          </AnimatedSection>

          {/* Contact info panel — 1/3 width on desktop */}
          <AnimatedSection delay={0.15}>
            <div
              className="flex h-full flex-col gap-6 p-8"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #EEE7E0',
              }}
            >
              {/* Hotel name */}
              <h3
                className="text-base font-bold uppercase tracking-wide"
                style={{ color: '#0F2966' }}
              >
                Hotel Boutique Nueva Valladolid
              </h3>

              {/* Address */}
              <InfoRow
                icon={<MapPin className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: '#0F2966' }} />}
                label={t('contact.address_label')}
                value={CONTACT.address}
              />

              {/* Phone */}
              <InfoRow
                icon={<Phone className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: '#0F2966' }} />}
                label={t('contact.phone_label')}
                href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
                value={CONTACT.phone}
              />

              {/* Email */}
              <InfoRow
                icon={<Mail className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: '#0F2966' }} />}
                label={t('contact.email_label')}
                href={`mailto:${CONTACT.email}`}
                value={CONTACT.email}
              />

              {/* Hours — matches real site footer: ABIERTO LAS 24 HS / CHECK-IN / CHECK-OUT */}
              <InfoRow
                icon={<Clock className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: '#0F2966' }} />}
                label={es ? 'Horarios' : 'Hours'}
                value={
                  es
                    ? 'Abierto 24 hs · Check-in: 2:00 PM · Check-out: 11:00 AM'
                    : 'Open 24 hrs · Check-in: 2:00 PM · Check-out: 11:00 AM'
                }
              />

              {/* Google Maps link */}
              <a
                href={CONTACT.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-colors"
                style={{ color: '#0F2966' }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLAnchorElement).style.color = '#61988E'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLAnchorElement).style.color = '#0F2966'
                }}
              >
                <MapPin className="h-4 w-4" />
                {es ? 'Ver en Google Maps' : 'View on Google Maps'}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}

function InfoRow({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode
  label: string
  value: string
  href?: string
}) {
  const content = href ? (
    <a
      href={href}
      className="mt-1 block text-sm transition-colors"
      style={{ color: '#666666' }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLAnchorElement).style.color = '#0F2966'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLAnchorElement).style.color = '#666666'
      }}
    >
      {value}
    </a>
  ) : (
    <p className="mt-1 text-sm" style={{ color: '#666666' }}>
      {value}
    </p>
  )

  return (
    <div className="flex items-start gap-3">
      {icon}
      <div>
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0F2966' }}>
          {label}
        </p>
        {content}
      </div>
    </div>
  )
}
